import React, { useState, useRef, useEffect, useCallback } from "react";
import { Stage, Layer, Rect, Transformer, Text, Image as KonvaImage } from "react-konva";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, Move, Square, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

/**
 * ZoneMarker component for drawing bounding boxes on document images
 * Used in Zone OCR to mark specific areas for field extraction
 */
export default function ZoneMarker({ 
  imageBase64, 
  fields, 
  onZonesUpdate,
  width = 700,
  height = 900
}) {
  const [image, setImage] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [zones, setZones] = useState([]);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [newZone, setNewZone] = useState(null);
  const [activeFieldIndex, setActiveFieldIndex] = useState(null);
  const [scale, setScale] = useState(1);
  
  const stageRef = useRef(null);
  const transformerRef = useRef(null);

  // Load image when base64 changes
  useEffect(() => {
    if (imageBase64) {
      const img = new window.Image();
      img.onload = () => {
        setImage(img);
        setImageSize({ width: img.width, height: img.height });
        
        // Calculate initial scale to fit within container
        const scaleX = width / img.width;
        const scaleY = height / img.height;
        const initialScale = Math.min(scaleX, scaleY, 1);
        setScale(initialScale);
      };
      img.src = `data:image/png;base64,${imageBase64}`;
    }
  }, [imageBase64, width, height]);

  // Load existing zones from fields
  useEffect(() => {
    if (fields && fields.length > 0) {
      const existingZones = fields
        .map((field, index) => {
          if (field.position_zone) {
            return {
              id: `zone_${index}`,
              fieldIndex: index,
              fieldName: field.field_name,
              displayName: field.display_name || field.field_name,
              x: field.position_zone.x,
              y: field.position_zone.y,
              width: field.position_zone.width,
              height: field.position_zone.height,
            };
          }
          return null;
        })
        .filter(Boolean);
      setZones(existingZones);
    }
  }, [fields]);

  // Update transformer when selection changes
  useEffect(() => {
    if (transformerRef.current && selectedZoneId) {
      const stage = stageRef.current;
      const selectedNode = stage.findOne(`#${selectedZoneId}`);
      if (selectedNode) {
        transformerRef.current.nodes([selectedNode]);
        transformerRef.current.getLayer().batchDraw();
      }
    } else if (transformerRef.current) {
      transformerRef.current.nodes([]);
    }
  }, [selectedZoneId]);

  // Convert pixel coordinates to percentages
  const pixelToPercent = useCallback((px, dimension) => {
    return (px / (imageSize[dimension] * scale)) * 100;
  }, [imageSize, scale]);

  // Convert percentages to pixel coordinates
  const percentToPixel = useCallback((percent, dimension) => {
    return (percent / 100) * (imageSize[dimension] * scale);
  }, [imageSize, scale]);

  // Handle mouse down - start drawing
  const handleMouseDown = (e) => {
    if (activeFieldIndex === null) return;
    
    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    
    // Check if clicking on existing zone
    const clickedOnZone = e.target.name() === "zone";
    if (clickedOnZone) {
      setSelectedZoneId(e.target.id());
      return;
    }
    
    // Deselect if clicking on stage
    if (e.target === stage) {
      setSelectedZoneId(null);
    }
    
    // Start drawing new zone
    setIsDrawing(true);
    setNewZone({
      x: point.x,
      y: point.y,
      width: 0,
      height: 0
    });
  };

  // Handle mouse move - resize zone while drawing
  const handleMouseMove = (e) => {
    if (!isDrawing || !newZone) return;
    
    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    
    setNewZone({
      ...newZone,
      width: point.x - newZone.x,
      height: point.y - newZone.y
    });
  };

  // Handle mouse up - finish drawing
  const handleMouseUp = () => {
    if (!isDrawing || !newZone || activeFieldIndex === null) {
      setIsDrawing(false);
      setNewZone(null);
      return;
    }
    
    // Only create zone if it has meaningful size
    if (Math.abs(newZone.width) > 10 && Math.abs(newZone.height) > 10) {
      // Normalize coordinates (handle negative width/height from drawing right-to-left)
      let x = newZone.x;
      let y = newZone.y;
      let w = newZone.width;
      let h = newZone.height;
      
      if (w < 0) {
        x = x + w;
        w = Math.abs(w);
      }
      if (h < 0) {
        y = y + h;
        h = Math.abs(h);
      }
      
      const field = fields[activeFieldIndex];
      const zoneId = `zone_${activeFieldIndex}`;
      
      // Remove existing zone for this field
      const filteredZones = zones.filter(z => z.fieldIndex !== activeFieldIndex);
      
      // Add new zone
      const newZoneData = {
        id: zoneId,
        fieldIndex: activeFieldIndex,
        fieldName: field.field_name,
        displayName: field.display_name || field.field_name,
        x: pixelToPercent(x, "width"),
        y: pixelToPercent(y, "height"),
        width: pixelToPercent(w, "width"),
        height: pixelToPercent(h, "height"),
      };
      
      const updatedZones = [...filteredZones, newZoneData];
      setZones(updatedZones);
      setSelectedZoneId(zoneId);
      
      // Notify parent
      notifyZonesUpdate(updatedZones);
    }
    
    setIsDrawing(false);
    setNewZone(null);
    setActiveFieldIndex(null);
  };

  // Handle zone transform (resize/move)
  const handleZoneTransform = (zoneId) => {
    const stage = stageRef.current;
    const node = stage.findOne(`#${zoneId}`);
    if (!node) return;
    
    const zone = zones.find(z => z.id === zoneId);
    if (!zone) return;
    
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    
    // Reset scale and apply to width/height
    node.scaleX(1);
    node.scaleY(1);
    
    const updatedZones = zones.map(z => {
      if (z.id === zoneId) {
        return {
          ...z,
          x: pixelToPercent(node.x(), "width"),
          y: pixelToPercent(node.y(), "height"),
          width: pixelToPercent(node.width() * scaleX, "width"),
          height: pixelToPercent(node.height() * scaleY, "height"),
        };
      }
      return z;
    });
    
    setZones(updatedZones);
    notifyZonesUpdate(updatedZones);
  };

  // Handle zone drag
  const handleZoneDragEnd = (zoneId, e) => {
    const updatedZones = zones.map(z => {
      if (z.id === zoneId) {
        return {
          ...z,
          x: pixelToPercent(e.target.x(), "width"),
          y: pixelToPercent(e.target.y(), "height"),
        };
      }
      return z;
    });
    
    setZones(updatedZones);
    notifyZonesUpdate(updatedZones);
  };

  // Delete selected zone
  const handleDeleteZone = () => {
    if (!selectedZoneId) return;
    
    const updatedZones = zones.filter(z => z.id !== selectedZoneId);
    setZones(updatedZones);
    setSelectedZoneId(null);
    notifyZonesUpdate(updatedZones);
  };

  // Notify parent component of zone updates
  const notifyZonesUpdate = (updatedZones) => {
    if (onZonesUpdate) {
      const zonesMap = {};
      updatedZones.forEach(z => {
        zonesMap[z.fieldIndex] = {
          x: z.x,
          y: z.y,
          width: z.width,
          height: z.height
        };
      });
      onZonesUpdate(zonesMap);
    }
  };

  // Get color for zone based on field index
  const getZoneColor = (index) => {
    const colors = [
      "#E01626", // Red (brand)
      "#2563eb", // Blue
      "#16a34a", // Green
      "#9333ea", // Purple
      "#ea580c", // Orange
      "#0891b2", // Cyan
      "#be185d", // Pink
      "#4f46e5", // Indigo
    ];
    return colors[index % colors.length];
  };

  if (!imageBase64) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted/30 rounded-lg border-2 border-dashed">
        <div className="text-center text-muted-foreground">
          <Square className="h-10 w-10 mx-auto mb-2 opacity-50" />
          <p>Upload a sample document to mark zones</p>
        </div>
      </div>
    );
  }

  const scaledWidth = imageSize.width * scale;
  const scaledHeight = imageSize.height * scale;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Select field to mark:</span>
          <div className="flex flex-wrap gap-1">
            {fields.map((field, index) => {
              const hasZone = zones.some(z => z.fieldIndex === index);
              return (
                <Button
                  key={index}
                  size="sm"
                  variant={activeFieldIndex === index ? "default" : hasZone ? "secondary" : "outline"}
                  className={`text-xs ${hasZone && activeFieldIndex !== index ? "border-green-500" : ""}`}
                  onClick={() => setActiveFieldIndex(activeFieldIndex === index ? null : index)}
                  style={{ 
                    borderColor: hasZone ? getZoneColor(index) : undefined,
                    backgroundColor: activeFieldIndex === index ? getZoneColor(index) : undefined
                  }}
                >
                  {field.display_name || field.field_name}
                  {hasZone && <span className="ml-1 text-green-500">✓</span>}
                </Button>
              );
            })}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setScale(s => Math.min(s + 0.1, 2))}
            disabled={scale >= 2}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground w-12 text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setScale(s => Math.max(s - 0.1, 0.3))}
            disabled={scale <= 0.3}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => {
              const scaleX = width / imageSize.width;
              const scaleY = height / imageSize.height;
              setScale(Math.min(scaleX, scaleY, 1));
            }}
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          {selectedZoneId && (
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={handleDeleteZone}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete Zone
            </Button>
          )}
        </div>
      </div>
      
      {/* Instructions */}
      {activeFieldIndex !== null && (
        <div className="p-2 bg-blue-50 rounded text-sm text-blue-800">
          <strong>Drawing mode:</strong> Click and drag on the document to mark the zone for "{fields[activeFieldIndex]?.display_name || fields[activeFieldIndex]?.field_name}"
        </div>
      )}
      
      {/* Canvas */}
      <div 
        className="border rounded-lg overflow-auto bg-gray-100"
        style={{ maxHeight: height + 50, maxWidth: "100%" }}
      >
        <Stage
          ref={stageRef}
          width={Math.max(scaledWidth, width)}
          height={Math.max(scaledHeight, height)}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          style={{ cursor: activeFieldIndex !== null ? "crosshair" : "default" }}
        >
          <Layer>
            {/* Background image */}
            {image && (
              <KonvaImage
                image={image}
                width={scaledWidth}
                height={scaledHeight}
              />
            )}
            
            {/* Existing zones */}
            {zones.map((zone) => (
              <React.Fragment key={zone.id}>
                <Rect
                  id={zone.id}
                  name="zone"
                  x={percentToPixel(zone.x, "width")}
                  y={percentToPixel(zone.y, "height")}
                  width={percentToPixel(zone.width, "width")}
                  height={percentToPixel(zone.height, "height")}
                  fill={getZoneColor(zone.fieldIndex) + "20"}
                  stroke={getZoneColor(zone.fieldIndex)}
                  strokeWidth={2}
                  draggable
                  onClick={() => setSelectedZoneId(zone.id)}
                  onTap={() => setSelectedZoneId(zone.id)}
                  onDragEnd={(e) => handleZoneDragEnd(zone.id, e)}
                  onTransformEnd={() => handleZoneTransform(zone.id)}
                />
                {/* Zone label */}
                <Text
                  x={percentToPixel(zone.x, "width") + 4}
                  y={percentToPixel(zone.y, "height") + 4}
                  text={zone.displayName}
                  fontSize={12}
                  fill={getZoneColor(zone.fieldIndex)}
                  fontStyle="bold"
                />
              </React.Fragment>
            ))}
            
            {/* Zone being drawn */}
            {isDrawing && newZone && (
              <Rect
                x={newZone.x}
                y={newZone.y}
                width={newZone.width}
                height={newZone.height}
                fill={getZoneColor(activeFieldIndex) + "30"}
                stroke={getZoneColor(activeFieldIndex)}
                strokeWidth={2}
                dash={[5, 5]}
              />
            )}
            
            {/* Transformer for selected zone */}
            <Transformer
              ref={transformerRef}
              boundBoxFunc={(oldBox, newBox) => {
                // Limit resize
                if (newBox.width < 20 || newBox.height < 20) {
                  return oldBox;
                }
                return newBox;
              }}
            />
          </Layer>
        </Stage>
      </div>
      
      {/* Zone summary */}
      {zones.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-muted-foreground">Marked zones:</span>
          {zones.map(zone => (
            <Badge 
              key={zone.id} 
              variant="outline"
              style={{ borderColor: getZoneColor(zone.fieldIndex), color: getZoneColor(zone.fieldIndex) }}
            >
              {zone.displayName}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
